/**
 * The eleven picked attack looks (`attack-animations` 3.1, the developer's picks of 2026-10-10),
 * ported from the 0.2 prototype onto the board's {@link FxScene}: strike A, tracer A, volley A,
 * heavy A, lobbed A, missile A, spray B, beam B, swarm A, glyph A and the Blast detonation B. The
 * damage-type palette accents ride every hit, and a Smart weapon's reticle closes on its target
 * before the shot leaves.
 *
 * Travel never reads the outcome: it plays before the dice land. A family that holds over its
 * target through the dice (swarm's drones, the Smart reticle) keeps a pending entry keyed by
 * attacker and target, which the outcome resolves when it arrives; an entry nobody resolves gives
 * up after {@link PENDING_TTL_MS}. The token perturbations (strike's lunge, heavy's recoil, a miss's
 * dodge) are not here: they move token containers, which the animator owns (`pixiAnimator.ts`).
 *
 * Board-scale fixes over the prototype (its known weak spots): swarm drones are drawn about half
 * again as large with a dark outline, glyph A's stream is larger and brighter with a glowing
 * uplink, and volley and heavy misses kick up a visible cluster of dust and a crater rather than a
 * puff the size of a pellet.
 */
import { Graphics, Text } from "pixi.js";
import type { AttackFamily, AttackFx, AttackPalette } from "@lancer-vtt/domain";
import type { AttackOutcomeItem, AttackTravelItem, FxUnit } from "../../../lib/encounter/attackFxItems";
import {
  FxScene,
  TAU,
  add,
  ang,
  chunks,
  clamp01,
  disc,
  easeIn,
  easeInOut,
  easeOut,
  easeOutCubic,
  embers,
  flat,
  len,
  lerp,
  mover,
  mul,
  norm,
  perp,
  polar,
  puffs,
  qbez,
  ring,
  sparks,
  sub,
  v2,
  type V,
} from "./fxScene";

const FONT = "JetBrains Mono, monospace";

/** A pending entry nobody resolves (a skipped outcome) lets go after this long. */
export const PENDING_TTL_MS = 8000;

type Palette = { core: number; glow: number; trail: number; spark: number; debris: number; smoke?: true; embers?: true; haze?: true };

/** Damage-type palettes (research §4 "Damage type as palette"); `tech` is the glyph family's own. */
const PALETTES: Record<AttackPalette | "tech", Palette> = {
  kinetic: { core: 0xfff3c0, glow: 0xffd76a, trail: 0xffd76a, spark: 0xfff6e0, debris: 0xb9ab8c },
  energy: { core: 0xeef8ff, glow: 0x5fd8ff, trail: 0x9a7cff, spark: 0x8fe6ff, debris: 0x7f90b8 },
  explosive: { core: 0xfff1c2, glow: 0xff9a2a, trail: 0xff7a1a, spark: 0xffae3d, debris: 0x6b5a44, smoke: true },
  burn: { core: 0xffe2a8, glow: 0xff5a1f, trail: 0xff3b1a, spark: 0xff8a3a, debris: 0x5a3a28, embers: true },
  heat: { core: 0xffd6ec, glow: 0xff3a8a, trail: 0xd8306a, spark: 0xff6ab0, debris: 0x6a3a50, haze: true },
  tech: { core: 0xeafff5, glow: 0x3dffce, trail: 0x18e6a8, spark: 0x3dffce, debris: 0x2a6a58 },
};

const DUST = 0x9a907e;
const CRATER = 0x15120d;
const SCORCH = 0x16120c;

/** The target-shake strength a family's hit carries (heavy hits harder). */
export const FAMILY_SHAKE: Partial<Record<AttackFamily, number>> = { heavy: 1.6 };

/** One entry in the debug log an e2e flow reads (`__combatBoardDebug.getAttackFxLog`). */
export type AttackFxLogEntry = { phase: "travel" | "outcome"; family: AttackFamily; outcome?: string; area?: true; reduced?: true; at: number };

type Pending = { born: number; resAt?: number; resKind?: "hit" | "miss"; pellets?: V[] };

export type AttackFxLooks = {
  travel(item: AttackTravelItem): void;
  outcome(item: AttackOutcomeItem): void;
  clear(): void;
  log(): AttackFxLogEntry[];
};

const LOG_CAP = 200;

/**
 * The attack looks over one scene. `cell` is the board's cell size in world px (the cone, burst
 * and Blast looks walk the grid).
 */
export function createAttackFxLooks(scene: FxScene, cell: number): AttackFxLooks {
  const s = scene;
  const pending = new Map<string, Pending>();
  const logEntries: AttackFxLogEntry[] = [];
  const keyOf = (attacker: string, target: string): string => `${attacker}>${target}`;

  function record(entry: Omit<AttackFxLogEntry, "at">): void {
    logEntries.push({ ...entry, at: performance.now() });
    if (logEntries.length > LOG_CAP) logEntries.splice(0, logEntries.length - LOG_CAP);
  }

  function paletteFor(fx: AttackFx): Palette {
    return fx.family === "glyph" ? PALETTES.tech : PALETTES[fx.palette];
  }

  /** Drop holds nobody resolved in time (lazily, so a stale entry never keeps the scene busy). */
  function prune(): void {
    for (const [key, p] of pending) if (s.t - p.born > PENDING_TTL_MS) pending.delete(key);
  }

  function pendingFor(attacker: string, target: string): Pending {
    prune();
    const key = keyOf(attacker, target);
    let p = pending.get(key);
    if (p === undefined) {
      p = { born: s.t };
      pending.set(key, p);
    }
    return p;
  }

  // ── geometry around one attack ──────────────────────────────────────────────────────────────
  const dirOf = (a: V, t: V): V => norm(sub(t, a));
  const muzzle = (a: FxUnit, t: V, k = 0.42): V => add(a, mul(dirOf(a, t), Math.max(a.r, cell * k)));
  const edge = (a: V, t: FxUnit, k = 0.42): V => sub(t, mul(dirOf(a, t), Math.max(t.r * 0.9, cell * k)));
  /** Where a miss goes: beside the target, on the side alternating per target. */
  const missAt = (a: V, t: FxUnit, i: number): V => add(t, mul(perp(dirOf(a, t)), (t.r + cell * 0.55) * (i % 2 === 0 ? 1 : -1)));

  // ── shared outcome pieces ───────────────────────────────────────────────────────────────────
  function reticle(attacker: string, t: FxUnit, P: Palette): void {
    const pend = pendingFor(attacker, t.id);
    const r0 = t.r + cell * 0.55;
    const r1 = t.r + cell * 0.12;
    const g = new Graphics();
    const born = s.t;
    s.add(g, () => {
      g.clear();
      const age = s.t - born;
      const resAt = pend.resAt;
      let r = r0 + (r1 - r0) * easeOutCubic(clamp01(age / 150));
      let alpha = 0.9;
      let w = 2;
      let spread = 0;
      let color = P.glow;
      if (resAt !== undefined) {
        const q = s.t - resAt;
        if (q > 320) return false;
        if (pend.resKind === "miss") {
          spread = easeOut(clamp01(q / 260)) * cell * 0.6;
          alpha = 0.9 * (1 - clamp01(q / 260));
        } else {
          w = q < 110 ? 3.5 : 2;
          alpha = q < 110 ? 1 : 0.9 * (1 - clamp01((q - 110) / 200));
          if (q < 110) color = 0xffffff;
        }
      } else if (age > 150) alpha = 0.55 + 0.4 * Math.abs(Math.sin(age / 90));
      r += spread;
      const L = cell * 0.2;
      for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        const cx = t.x + sx * r;
        const cy = t.y + sy * r;
        g.moveTo(cx - sx * L, cy).lineTo(cx, cy).lineTo(cx, cy - sy * L);
      }
      g.stroke({ width: w, color, alpha });
      return age < PENDING_TTL_MS;
    });
  }

  function paletteAccent(fx: AttackFx, t: FxUnit, k: number): void {
    const P = paletteFor(fx);
    if (fx.family === "glyph") return;
    if (fx.palette === "kinetic") puffs(s, { at: v2(t.x, t.y + 6), n: 4, color: 0xa89a7e, r: 3, grow: 7 * k, life: 480, alpha: 0.4 });
    if (fx.palette === "energy") ring(s, { at: t, r0: cell * 0.25, r1: (t.r + cell * 0.3) * k, w: 2, color: P.glow, life: 230 });
    if (P.smoke) puffs(s, { at: t, n: 5, color: 0x4f4840, r: 5 * k, grow: 10 * k, life: 850, alpha: 0.5, drift: v2(4, -10) });
    if (P.embers) {
      embers(s, { at: t, n: 9, color: P.spark, life: 700 });
      s.timed(420, (g, q) => {
        for (let j = -1; j <= 1; j++) {
          const fxX = t.x + j * 8;
          const h = (12 + 6 * Math.sin(q * 30 + j)) * (1 - q) * k;
          g.poly([fxX - 4, t.y + 6, fxX + 4, t.y + 6, fxX + Math.sin(q * 20 + j) * 2, t.y + 6 - h]);
        }
        g.fill({ color: P.glow, alpha: 0.75 * (1 - q) });
      });
    }
    if (P.haze) {
      s.timed(520, (g, q) => {
        const r = t.r * (1.05 + 0.12 * Math.sin(q * 26) + 0.2 * q);
        g.rect(t.x - r, t.y - r, r * 2, r * 2).stroke({ width: 3, color: P.glow, alpha: 0.7 * (1 - q) });
        g.rect(t.x - t.r, t.y - t.r, t.r * 2, t.r * 2).fill({ color: P.glow, alpha: 0.22 * (1 - q) });
      });
    }
  }

  function areaHit(fx: AttackFx, o: { from: V; t: FxUnit; k: number }): void {
    const { from, t, k } = o;
    const P = paletteFor(fx);
    const d = sub(t, from);
    const a = len(d) < 4 ? s.rng() * TAU : ang(d);
    sparks(s, { at: t, n: 8, color: P.spark, dist: cell * 0.55 * k, len: 7, life: 260, dir: a, spread: 2.2 });
    disc(s, { at: t, r0: 4, r1: cell * 0.35 * k, color: P.glow, life: 180, alpha: 0.7 });
  }

  function areaMiss(from: V, t: FxUnit): void {
    const d = len(sub(t, from)) < 4 ? v2(1, 0) : norm(sub(t, from));
    const face = sub(t, mul(d, t.r + cell * 0.05));
    puffs(s, { at: face, n: 6, color: DUST, r: 3, grow: 9, spread: 8, life: 560, alpha: 0.5 });
    sparks(s, { at: face, n: 5, color: 0xcfc6b2, dist: cell * 0.4, len: 5, life: 220, dir: ang(d) + Math.PI, spread: 1.6, alpha: 0.8 });
  }

  /** Cell centres within `size` of `centre`, grouped by Chebyshev ring (0 = the centre cell). */
  function rings(centre: V, size: number): V[][] {
    const cx = Math.floor(centre.x / cell);
    const cy = Math.floor(centre.y / cell);
    return Array.from({ length: size + 1 }, (_, d) => {
      const out: V[] = [];
      for (let dx = -d; dx <= d; dx++) for (let dy = -d; dy <= d; dy++) if (Math.max(Math.abs(dx), Math.abs(dy)) === d) out.push(v2((cx + dx) * cell, (cy + dy) * cell));
      return out;
    });
  }

  function cellFlash(corner: V, P: Palette, delay: number): void {
    s.at(delay, () =>
      s.timed(300, (g, p) => {
        const inset = 2 + 6 * p;
        g.rect(corner.x + inset, corner.y + inset, cell - inset * 2, cell - inset * 2).fill({ color: p < 0.15 ? P.core : P.glow, alpha: 0.5 * (1 - p) });
      }),
    );
  }

  /** Detonation look B: cells flash ring by ring from the centre, a square shockwave, debris, scorched cells. */
  function detonate(fx: AttackFx, at: V, reduced: boolean): void {
    const P = paletteFor(fx);
    const size = fx.area?.size ?? 1;
    const R = (size + 0.5) * cell;
    rings(at, size).forEach((cells, d) => {
      for (const c of cells) {
        cellFlash(c, P, reduced ? 0 : d * 55);
        if (reduced) continue;
        s.at(d * 55 + 120, () => s.timed(1300, (g, q) => {
          g.rect(c.x + 3, c.y + 3, cell - 6, cell - 6).fill({ color: 0x120f0a, alpha: 0.32 * (1 - easeIn(q)) });
        }, "under"));
      }
    });
    if (reduced) return;
    s.timed(420, (g, q) => {
      const h = cell * 0.5 + (R + cell * 0.6 - cell * 0.5) * easeOut(q);
      g.rect(at.x - h, at.y - h, h * 2, h * 2).stroke({ width: 4 * (1 - q * 0.6), color: P.core, alpha: 1 - q });
    });
    chunks(s, { at, n: 14, color: P.debris, size: 4, dist: R * 1.1, life: 520 });
    if (P.smoke) puffs(s, { at, n: 6, color: 0x4f4840, r: 6, grow: 10, spread: R * 0.4, life: 1100, alpha: 0.45, drift: v2(4, -10) });
    if (P.embers) embers(s, { at, n: 16, color: P.spark, life: 900 });
  }

  // ── travel per family ───────────────────────────────────────────────────────────────────────
  function streak(g: Graphics, pos: V, o: { from: V; d: V; tail: number; w: number; P: Palette }): void {
    const { from } = o;
    const tl = len(sub(pos, from)) > o.tail ? sub(pos, mul(o.d, o.tail)) : from;
    g.moveTo(tl.x, tl.y).lineTo(pos.x, pos.y).stroke({ width: o.w * 2.4, color: o.P.glow, alpha: 0.32, cap: "round" });
    g.moveTo(tl.x, tl.y).lineTo(pos.x, pos.y).stroke({ width: o.w, color: o.P.core, cap: "round" });
  }

  function travelLook(item: AttackTravelItem): void {
    const { fx, attacker: a, to, targets } = item;
    const P = paletteFor(fx);
    const t0 = targets[0] ?? { id: "", ...to, r: cell * 0.4 };
    const d = dirOf(a, to);
    switch (fx.family) {
      case "strike": {
        if (fx.thrown === true) {
          const from = muzzle(a, t0);
          const end = edge(a, t0);
          mover(s, { from, to: end, ms: 240, draw: (g, pos, { age }) => {
            const b = blade(pos, age * 0.045, 1);
            g.poly(b.glow).fill({ color: P.glow, alpha: 0.35 });
            g.poly(b.blade).fill({ color: P.core });
            g.arc(pos.x, pos.y, 13, age * 0.045 + Math.PI * 0.2, age * 0.045 + Math.PI * 0.9).stroke({ width: 1.5, color: P.glow, alpha: 0.5 });
          } });
          return;
        }
        // The lunge itself is the animator's; a glint flares off the weapon at full reach.
        const glint = add(a, mul(d, a.r + cell * 0.35));
        s.at(120, () => disc(s, { at: glint, r0: 2, r1: 8, color: P.glow, life: 260, alpha: 0.85 }));
        return;
      }
      case "tracer": {
        const from = muzzle(a, t0);
        const end = edge(a, t0);
        disc(s, { at: from, r0: 2, r1: 8, color: P.glow, life: 120, alpha: 0.9 });
        mover(s, { from, to: end, ms: 160, draw: (g, pos) => {
          streak(g, pos, { from, d, tail: 30, w: 2.2, P });
          g.circle(pos.x, pos.y, 2.6).fill({ color: P.core });
        } });
        return;
      }
      case "volley": {
        const from = muzzle(a, t0);
        const end = edge(a, t0, 0.3);
        const dist = len(sub(end, from));
        s.timed(110, (g, p) => {
          const aa = ang(d);
          const r = cell * 0.45 * easeOut(p);
          g.poly(flat([from, add(from, polar(r, aa - 0.35)), add(from, polar(r * 1.2, aa)), add(from, polar(r, aa + 0.35))])).fill({ color: P.glow, alpha: 0.75 * (1 - p) });
        });
        const pend = pendingFor(a.id, t0.id);
        pend.pellets = [];
        for (let j = 0; j < 6; j++) {
          const aa = ang(d) + (j - 2.5) * 0.055 + (s.rng() - 0.5) * 0.03;
          const pe = add(from, polar(dist * (0.95 + s.rng() * 0.1), aa));
          pend.pellets.push(pe);
          mover(s, { from, to: pe, ms: 200 * (0.9 + s.rng() * 0.2), draw: (g, pos) => {
            const tl = len(sub(pos, from)) > 12 ? sub(pos, polar(12, aa)) : from;
            g.moveTo(tl.x, tl.y).lineTo(pos.x, pos.y).stroke({ width: 1.8, color: P.core, alpha: 0.95, cap: "round" });
          } });
        }
        return;
      }
      case "heavy": {
        const from = muzzle(a, t0, 0.5);
        const end = edge(a, t0);
        disc(s, { at: from, r0: 6, r1: 15, color: P.glow, life: 140, alpha: 0.9 });
        ring(s, { at: from, r0: 6, r1: 22, w: 3, color: P.core, life: 180 });
        mover(s, { from, to: end, ms: 260, draw: (g, pos) => {
          streak(g, pos, { from, d, tail: 36, w: 3.6, P });
          g.circle(pos.x, pos.y, 5).fill({ color: P.core });
        } });
        return;
      }
      case "lobbed": {
        lob(item, P);
        return;
      }
      case "missile": {
        targets.forEach((t, i) => s.at(i * 70, () => {
          const from = muzzle(a, t, 0.35);
          const end = edge(a, t);
          const dd = dirOf(a, t);
          const side = i % 2 === 0 ? 1 : -1;
          const ctrl = add(lerp(from, end, 0.45), mul(perp(dd), cell * 1.3 * side));
          let lastPuff = -99;
          let prev = from;
          mover(s, { from, to: end, ms: 380, path: (p) => qbez([from, ctrl, end], p), ease: easeInOut, draw: (g, pos, { age }) => {
            if (age - lastPuff > 24) {
              lastPuff = age;
              puffs(s, { at: pos, n: 1, color: 0x8f8a80, r: 2.5, grow: 5, spread: 1, life: 520, alpha: 0.45, drift: v2(0, -3), layer: "under" });
            }
            const dir = norm(sub(pos, prev));
            prev = pos;
            const nose = add(pos, mul(dir, 7));
            const nn = perp(dir);
            const back = sub(pos, mul(dir, 5));
            g.circle(back.x, back.y, 3 + s.rng() * 1.5).fill({ color: P.glow, alpha: 0.85 });
            g.poly(flat([nose, add(back, mul(nn, 3.5)), sub(back, mul(nn, 3.5))])).fill({ color: P.core });
          } });
        }));
        return;
      }
      case "spray": {
        sprayCells(item).forEach(([corner, ringIdx]) => cellFlash(corner, P, ringIdx * 45));
        if (fx.area?.kind === "burst") {
          s.timed(260, (g, q) => {
            const h = a.r + cell * (0.2 + (fx.area?.size ?? 1) * easeOut(q));
            g.rect(a.x - h, a.y - h, h * 2, h * 2).stroke({ width: 3, color: P.core, alpha: 1 - q });
          });
        }
        return;
      }
      case "beam": {
        beam(item, P);
        return;
      }
      case "swarm": {
        targets.forEach((t, i) => swarm(a, t, { P, count: targets.length > 1 ? Math.max(3, Math.ceil(7 / targets.length)) : 7, delay: i * 40 }));
        return;
      }
      case "glyph": {
        glyphStream(a, t0, P);
        return;
      }
    }
  }

  function blade(pos: V, rot: number, k: number): { blade: number[]; glow: number[] } {
    const u = polar(1, rot);
    const n = perp(u);
    const L = 11 * k;
    const W = 3 * k;
    return {
      blade: flat([add(pos, mul(u, L)), add(pos, mul(n, W)), sub(pos, mul(u, L * 0.6)), sub(pos, mul(n, W))]),
      glow: flat([add(pos, mul(u, L + 4)), add(pos, mul(n, W + 3)), sub(pos, mul(u, L * 0.6 + 4)), sub(pos, mul(n, W + 3))]),
    };
  }

  /** Lobbed look A: a parabolic shell with a ground shadow tracking under it. */
  function lob(item: AttackTravelItem, P: Palette): void {
    const { attacker: a, to } = item;
    const land = v2(to.x, to.y);
    const from = add(a, mul(dirOf(a, land), a.r * 0.6));
    const H = cell * 1.7;
    const arc = (p: number) => {
      const gp = lerp(from, land, p);
      const h = 4 * H * p * (1 - p);
      return { ground: gp, air: v2(gp.x, gp.y - h), h };
    };
    let lastPuff = -99;
    mover(s, { from, to: land, ms: 450, path: (p) => arc(p).air, draw: (g, pos, { p, age }) => {
      const ar = arc(p);
      const hh = ar.h / H;
      g.ellipse(ar.ground.x, ar.ground.y, 7 * (1 - hh * 0.5), 3.5 * (1 - hh * 0.5)).fill({ color: 0x000000, alpha: 0.45 * (1 - hh * 0.6) });
      if (P.smoke && age - lastPuff > 30) {
        lastPuff = age;
        puffs(s, { at: pos, n: 1, color: 0x8f8a80, r: 2, grow: 4, spread: 1, life: 420, alpha: 0.4, drift: v2(0, -2) });
      }
      g.circle(pos.x, pos.y, 4 * (1 + 0.45 * hh)).fill({ color: P.core });
    } });
  }

  /** Beam look B: charge-up at the muzzle, the beam extends cell by cell, three pulses, collapse. */
  function beam(item: AttackTravelItem, P: Palette): void {
    const { attacker: a, to } = item;
    const d = dirOf(a, to);
    const from = add(a, mul(d, a.r));
    // A Line runs its full length whatever cell was aimed at; a beam without one stops at its target.
    const end = item.fx.area?.kind === "line" ? add(a, mul(d, a.r + item.fx.area.size * cell)) : v2(to.x, to.y);
    const draw = (g: Graphics, head: V, o: { w: number; alpha: number }) => {
      const { w, alpha } = o;
      const f = 1 + 0.15 * Math.sin(s.t * 0.09);
      g.moveTo(from.x, from.y).lineTo(head.x, head.y).stroke({ width: 13 * w * f, color: P.glow, alpha: 0.2 * alpha });
      g.moveTo(from.x, from.y).lineTo(head.x, head.y).stroke({ width: 6 * w * f, color: P.glow, alpha: 0.55 * alpha });
      g.moveTo(from.x, from.y).lineTo(head.x, head.y).stroke({ width: 2.2 * w, color: P.core, alpha });
    };
    s.timed(200, (g, p) => {
      g.circle(from.x, from.y, 3 + 7 * p).fill({ color: P.glow, alpha: 0.4 + 0.5 * p });
      for (let j = 0; j < 10; j++) {
        const u = polar(22 * (1 - p) + 2, j * 0.63 + p * 3);
        g.circle(from.x + u.x, from.y + u.y, 1.3);
      }
      g.fill({ color: P.core, alpha: 0.9 });
    });
    s.at(200, () => {
      s.timed(440, (g, _p, age) => {
        const head = lerp(from, end, easeOut(clamp01(age / 120)));
        const coll = age > 340 ? (age - 340) / 100 : 0;
        draw(g, head, { w: 1 - coll, alpha: 1 - coll * 0.5 });
        if (age < 120) g.circle(head.x, head.y, 5).fill({ color: P.core });
        for (let j = 0; j < 3; j++) {
          const q = (age - 120 - j * 70) / 160;
          if (q > 0 && q < 1) {
            const pa = lerp(from, end, q);
            const pb = lerp(from, end, Math.min(1, q + 0.08));
            g.moveTo(pa.x, pa.y).lineTo(pb.x, pb.y).stroke({ width: 5, color: 0xffffff, alpha: 0.85 });
          }
        }
      });
      s.at(130, () => {
        s.timed(1300, (g, q) => {
          g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ width: 5, color: SCORCH, alpha: 0.5 * (1 - easeIn(q)) });
        }, "under");
        if (P.embers) for (let j = 1; j <= 4; j++) embers(s, { at: lerp(from, end, j / 5), n: 3, color: P.spark, life: 700 });
      });
    });
  }

  /** Spray look B's cells, each with the ring (distance from the shooter) it flashes on. */
  function sprayCells(item: AttackTravelItem): [V, number][] {
    const { attacker: a, to, fx } = item;
    const size = fx.area?.size ?? 3;
    const out: [V, number][] = [];
    const acx = Math.floor(a.x / cell);
    const acy = Math.floor(a.y / cell);
    const reachCells = Math.ceil(a.r / cell - 0.5) + size;
    const dir = dirOf(a, to);
    for (let dx = -reachCells; dx <= reachCells; dx++) {
      for (let dy = -reachCells; dy <= reachCells; dy++) {
        const corner = v2((acx + dx) * cell, (acy + dy) * cell);
        const centre = v2(corner.x + cell / 2, corner.y + cell / 2);
        const rel = sub(centre, a);
        const cheb = Math.max(Math.abs(rel.x), Math.abs(rel.y));
        if (cheb <= a.r) continue; // the shooter's own footprint
        const ringIdx = Math.round((cheb - a.r) / cell + 0.5);
        if (ringIdx < 1 || ringIdx > size) continue;
        if (fx.area?.kind !== "burst") {
          const cos = (rel.x * dir.x + rel.y * dir.y) / (len(rel) || 1);
          if (cos < Math.cos(0.5)) continue;
        }
        out.push([corner, ringIdx]);
      }
    }
    return out;
  }

  /** Swarm look A: drones on staggered curves converge, orbit through the dice, then dive in or peel off. */
  function swarm(a: FxUnit, t: FxUnit, o: { P: Palette; count: number; delay: number }): void {
    const { P, count } = o;
    const pend = pendingFor(a.id, t.id);
    const FLY = 400;
    const start = s.t + o.delay;
    const d = dirOf(a, t);
    const orbitR = t.r + cell * 0.32;
    const drones = Array.from({ length: count }, (_, i) => ({
      st: i * 30,
      from: add(muzzle(a, t, 0.3), polar(5, s.rng() * TAU)),
      ctrl: add(lerp(a, t, 0.5), mul(perp(d), (s.rng() * 2 - 1) * cell * 1.7)),
      th0: (i / count) * TAU,
      prev: null as V | null,
      popped: false,
    }));
    const gg = new Graphics();
    s.add(gg, () => {
      gg.clear();
      const resAt = pend.resAt;
      const kind = pend.resKind;
      let busy = resAt === undefined;
      drones.forEach((dr, i) => {
        const age = s.t - start - dr.st;
        if (age < 0) {
          busy = true;
          return;
        }
        const orbit = (aa: number) => add(t, polar(orbitR, dr.th0 + Math.max(0, aa - FLY) * 0.007));
        let pos: V;
        let alpha = 1;
        if (age < FLY) pos = qbez([dr.from, dr.ctrl, orbit(FLY)], easeInOut(age / FLY));
        else if (resAt === undefined) pos = orbit(age);
        else {
          const base = orbit(resAt - start - dr.st);
          const b = s.t - resAt - i * 22;
          if (kind === "miss") {
            const q = clamp01(b / 460);
            const out = polar(1, dr.th0 + (resAt - start - dr.st - FLY) * 0.007 + 0.5);
            pos = add(base, mul(out, cell * 1.9 * easeIn(q)));
            alpha = 1 - q;
            if (q < 1) busy = true;
          } else {
            const q = clamp01(b / 120);
            pos = lerp(base, t, easeIn(q));
            if (q < 1) busy = true;
            else {
              alpha = 0;
              if (!dr.popped) {
                dr.popped = true;
                disc(s, { at: v2(t.x + (s.rng() - 0.5) * 14, t.y + (s.rng() - 0.5) * 14), r0: 2, r1: 10, color: P.glow, life: 160, alpha: 0.9 });
              }
            }
          }
        }
        const dir = dr.prev ? norm(sub(pos, dr.prev)) : d;
        dr.prev = pos;
        if (alpha <= 0) return;
        // Board scale: half again the prototype's size, with a dark outline so a drone reads over any token.
        const nose = add(pos, mul(dir, 8));
        const nn = perp(dir);
        const back = sub(pos, mul(dir, 5));
        const body = flat([nose, add(back, mul(nn, 5.5)), pos, sub(back, mul(nn, 5.5))]);
        gg.poly(body).fill({ color: P.core, alpha }).stroke({ width: 1, color: 0x0b0e13, alpha: alpha * 0.8 });
        gg.circle(back.x, back.y, 2.4).fill({ color: P.glow, alpha: alpha * 0.9 });
      });
      if (!busy) pending.delete(keyOf(a.id, t.id));
      return busy && s.t - start < PENDING_TTL_MS;
    });
  }

  /** Glyph look A: a stream of mono glyphs flows down a glowing uplink to the target. */
  function glyphStream(a: FxUnit, t: FxUnit, P: Palette): void {
    const from = muzzle(a, t);
    const end = edge(a, t);
    const chars = "01<>/#{}=+:*";
    s.timed(460, (g, p) => {
      g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ width: 4, color: P.glow, alpha: 0.18 * (1 - p * p) });
      g.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ width: 1.2, color: P.glow, alpha: 0.6 * (1 - p * p) });
    });
    for (let j = 0; j < 22; j++) {
      s.at(j * 11, () => {
        const txt = new Text({ text: chars[Math.floor(s.rng() * chars.length)]!, style: { fontFamily: FONT, fontSize: 14, fontWeight: "700", fill: j % 3 === 0 ? P.core : P.glow, stroke: { color: 0x0b0e13, width: 2 } } });
        txt.anchor.set(0.5);
        const wob = (s.rng() - 0.5) * 10;
        s.add(txt, (age) => {
          const q = clamp01(age / 210);
          const pos = lerp(from, end, q);
          txt.position.set(pos.x, pos.y + wob * Math.sin(q * 6));
          txt.alpha = q > 0.85 ? (1 - q) / 0.15 : 1;
          return age < 210;
        });
      });
    }
    s.at(430, () => ring(s, { at: t, r0: t.r + cell * 0.05, r1: t.r + cell * 0.25, w: 2, color: P.glow, life: 260, alpha: 0.75 }));
  }

  // ── outcome per family ──────────────────────────────────────────────────────────────────────
  function crescent(fx: AttackFx, c: V, o: { d: V; k: number; alpha?: number }): void {
    const P = paletteFor(fx);
    const { d, k, alpha = 1 } = o;
    const R = cell * 0.66 * k;
    const base = ang(d);
    const centre = sub(c, mul(d, cell * 0.3 * k));
    const a0 = base - 1.3;
    const span = 2.6;
    const sweep = 110;
    const life = 380;
    s.timed(life, (g, _p, age) => {
      const q = clamp01(age / sweep);
      const fade = age > sweep ? (age - sweep) / (life - sweep) : 0;
      const head = a0 + span * easeOut(q);
      const tail = Math.max(a0, head - span * 0.85);
      if (head - tail < 0.02) return;
      const N = 20;
      const outer: V[] = [];
      const inner: V[] = [];
      const og: V[] = [];
      const ig: V[] = [];
      for (let j = 0; j <= N; j++) {
        const u = j / N;
        const at = tail + (head - tail) * u;
        const w = 8 * k * Math.pow(u, 0.7) * (u > 0.9 ? (1 - u) / 0.1 : 1) + 0.5;
        outer.push(add(centre, polar(R + w * 0.5, at)));
        inner.push(add(centre, polar(R - w * 0.5, at)));
        og.push(add(centre, polar(R + w * 1.3, at)));
        ig.push(add(centre, polar(R - w * 1.3, at)));
      }
      g.poly(flat(og.concat(ig.reverse()))).fill({ color: P.glow, alpha: 0.32 * (1 - fade) * alpha });
      g.poly(flat(outer.concat(inner.reverse()))).fill({ color: P.core, alpha: (1 - fade) * alpha });
    });
  }

  function hitLook(item: AttackOutcomeItem, k: number): void {
    const { fx, attacker: a, at: t } = item;
    const P = paletteFor(fx);
    const d = dirOf(a, t);
    switch (fx.family) {
      case "strike": {
        if (fx.thrown === true) {
          const at = edge(a, t, 0.25);
          const b = blade(at, ang(d) + 0.3, k);
          s.timed(520, (g, p) => g.poly(b.blade).fill({ color: P.core, alpha: 1 - easeIn(p) }));
          sparks(s, { at, n: 7, color: P.spark, dist: cell * 0.5 * k, dir: ang(d) + Math.PI, spread: 2.2 });
          ring(s, { at, r0: 3, r1: cell * 0.4 * k, w: 2, color: P.glow, life: 220 });
          return;
        }
        crescent(fx, t, { d, k });
        sparks(s, { at: t, n: 7, color: P.spark, dist: cell * 0.7 * k, len: 8, life: 260, dir: ang(d), spread: 1.8 });
        return;
      }
      case "tracer":
        sparks(s, { at: t, n: 9, color: P.spark, dist: cell * 0.55 * k, len: 8 });
        ring(s, { at: t, r0: 3, r1: cell * 0.4 * k, w: 2, color: P.glow, life: 220 });
        disc(s, { at: t, r0: 6 * k, r1: 2, color: P.core, life: 120 });
        return;
      case "volley":
        for (let j = 0; j < 6; j++) {
          const at = add(t, v2((s.rng() - 0.5) * 20 * k, (s.rng() - 0.5) * 20 * k));
          s.at(j * 14, () => {
            ring(s, { at, r0: 1, r1: 8 * k, w: 1.6, color: P.glow, life: 180 });
            sparks(s, { at, n: 3, color: P.spark, dist: 11 * k, len: 4, life: 160 });
          });
        }
        return;
      case "heavy":
        ring(s, { at: t, r0: 6, r1: cell * 0.8 * k, w: 6, color: P.glow, life: 320 });
        disc(s, { at: t, r0: 6, r1: cell * 0.45 * k, color: P.glow, life: 220, alpha: 0.6 });
        disc(s, { at: t, r0: cell * 0.25 * k, r1: 3, color: P.core, life: 160 });
        chunks(s, { at: t, n: 8, color: P.debris, size: 3.5, dist: cell * 0.9 * k });
        return;
      case "lobbed":
        ring(s, { at: t, r0: 6, r1: cell * 0.75 * k, w: 5, color: P.glow, life: 320 });
        disc(s, { at: t, r0: cell * 0.3 * k, r1: 3, color: P.core, life: 180 });
        chunks(s, { at: t, n: 7, color: P.debris, dist: cell * 0.8 * k });
        return;
      case "missile":
        disc(s, { at: t, r0: 5, r1: cell * 0.45 * k, color: P.glow, life: 240, alpha: 0.8 });
        ring(s, { at: t, r0: 4, r1: cell * 0.7 * k, w: 3, color: P.core, life: 280 });
        sparks(s, { at: t, n: 8, color: P.spark, dist: cell * 0.6 * k });
        return;
      case "spray":
        areaHit(fx, { from: a, t, k });
        sparks(s, { at: t, n: 5, color: P.core, dist: cell * 0.5 * k, dir: ang(d), spread: 1 });
        return;
      case "beam":
        disc(s, { at: t, r0: 8 * k, r1: cell * 0.4 * k, color: P.glow, life: 220, alpha: 0.75 });
        sparks(s, { at: t, n: 5, color: P.spark, dist: cell * 0.6 * k, dir: -Math.PI / 2, spread: 0.9 });
        sparks(s, { at: t, n: 5, color: P.spark, dist: cell * 0.6 * k, dir: Math.PI / 2, spread: 0.9 });
        return;
      case "swarm":
        s.at(160, () => {
          sparks(s, { at: t, n: 10, color: P.spark, dist: cell * 0.5 * k, len: 5 });
          ring(s, { at: t, r0: 3, r1: cell * 0.5 * k, w: 2, color: P.glow, life: 220 });
        });
        return;
      case "glyph": {
        const R = t.r;
        let lastJ = -1;
        let bars: { y: number; h: number; dx: number; col: number }[] = [];
        s.timed(340 * k, (g, p, age) => {
          const jj = Math.floor(age / 40);
          if (jj !== lastJ) {
            lastJ = jj;
            bars = Array.from({ length: 5 }, () => ({ y: (s.rng() - 0.5) * R * 1.7, h: 2 + s.rng() * 4, dx: (s.rng() - 0.5) * 14 * k, col: s.rng() < 0.5 ? P.glow : 0x0b0e13 }));
          }
          for (const b of bars) g.rect(t.x - R + b.dx, t.y + b.y, R * 2, b.h).fill({ color: b.col, alpha: 0.75 * (1 - p * 0.5) });
          g.rect(t.x - R - 3 * k, t.y - R, R * 2, R * 2).stroke({ width: 1.5, color: 0xff3a8a, alpha: 0.8 * (1 - p) });
          g.rect(t.x - R + 3 * k, t.y - R, R * 2, R * 2).stroke({ width: 1.5, color: 0x3dffce, alpha: 0.8 * (1 - p) });
        });
        return;
      }
    }
  }

  function missLook(item: AttackOutcomeItem, i: number): void {
    const { fx, attacker: a, at: t } = item;
    const P = paletteFor(fx);
    const d = dirOf(a, t);
    const m = missAt(a, t, i);
    switch (fx.family) {
      case "strike": {
        if (fx.thrown === true) {
          const from = edge(a, t);
          mover(s, { from, to: m, ms: 130, ease: easeOut, draw: (g, pos, { age }) => g.poly(blade(pos, age * 0.03, 1).blade).fill({ color: P.core }) });
          s.at(130, () => {
            const b = blade(m, 0.6, 1);
            s.timed(600, (g, p) => g.poly(b.blade).fill({ color: P.core, alpha: 0.85 * (1 - easeIn(p)) }), "under");
            puffs(s, { at: m, n: 5, color: DUST, r: 3, grow: 7, life: 480, alpha: 0.5 });
          });
          return;
        }
        crescent(fx, m, { d, k: 1, alpha: 0.5 });
        return;
      }
      case "tracer": {
        const from = edge(a, t);
        mover(s, { from, to: m, ms: 90, draw: (g, pos) => {
          const tl = lerp(from, pos, 0.4);
          g.moveTo(tl.x, tl.y).lineTo(pos.x, pos.y).stroke({ width: 2, color: P.core, cap: "round" });
        } });
        s.at(90, () => {
          puffs(s, { at: m, n: 5, color: DUST, r: 3, grow: 7, life: 480, alpha: 0.5 });
          sparks(s, { at: m, n: 5, color: P.spark, dist: 12, len: 4, life: 180 });
        });
        return;
      }
      case "volley": {
        // Board scale: the pellets streak past and kick up a clear cluster of dust in the next cell.
        const pellets = pending.get(keyOf(a.id, t.id))?.pellets ?? [edge(a, t)];
        pellets.forEach((from, j) => {
          const end = add(m, v2((s.rng() - 0.5) * 24, (s.rng() - 0.5) * 24));
          s.at(j * 8, () => {
            mover(s, { from, to: end, ms: 90, draw: (g, pos) => {
              const tl = lerp(from, pos, 0.5);
              g.moveTo(tl.x, tl.y).lineTo(pos.x, pos.y).stroke({ width: 1.6, color: P.core, alpha: 0.9 });
            } });
            s.at(90, () => puffs(s, { at: end, n: 3, color: DUST, r: 3, grow: 7, spread: 4, life: 520, alpha: 0.6 }));
          });
        });
        s.at(110, () => ring(s, { at: m, r0: 4, r1: cell * 0.45, w: 2, color: DUST, life: 320, alpha: 0.7, layer: "under" }));
        return;
      }
      case "heavy": {
        // Board scale: a crater you can see, a skid on the ground and a spray of rock.
        const from = edge(a, t);
        mover(s, { from, to: m, ms: 80, draw: (g, pos) => g.circle(pos.x, pos.y, 4.5).fill({ color: P.core }) });
        s.timed(1400, (g, q) => g.moveTo(from.x, from.y).lineTo(m.x, m.y).stroke({ width: 4, color: CRATER, alpha: 0.55 * (1 - easeIn(q)) }), "under");
        s.at(80, () => {
          disc(s, { at: m, r0: 12, r1: 16, color: CRATER, life: 1400, alpha: 0.75, layer: "under" });
          ring(s, { at: m, r0: 6, r1: cell * 0.6, w: 3, color: 0x8f877a, life: 360, alpha: 0.8 });
          chunks(s, { at: m, n: 10, color: 0x6a5b44, size: 3.5, dist: cell * 0.7 });
          puffs(s, { at: m, n: 6, color: 0x8f877a, r: 4, grow: 11, life: 700, alpha: 0.55 });
        });
        return;
      }
      case "lobbed": {
        mover(s, { from: v2(t.x, t.y), to: m, ms: 160, draw: (g, pos, { p }) => g.circle(pos.x, pos.y - 14 * 4 * p * (1 - p), 4).fill({ color: P.core }) });
        s.at(160, () => {
          disc(s, { at: m, r0: 4, r1: 18, color: P.glow, life: 220, alpha: 0.75 });
          puffs(s, { at: m, n: 5, color: 0x6a6258, r: 4, grow: 8, life: 600, alpha: 0.5 });
        });
        return;
      }
      case "missile": {
        const from = edge(a, t);
        const end = add(m, mul(d, cell * 0.6));
        mover(s, { from, to: end, ms: 110, draw: (g, pos) => g.circle(pos.x, pos.y, 2.6).fill({ color: P.core }) });
        s.at(110, () => {
          disc(s, { at: end, r0: 3, r1: 15, color: P.glow, life: 220, alpha: 0.75 });
          puffs(s, { at: end, n: 4, color: 0x6a6258, r: 3, grow: 7, life: 600, alpha: 0.5 });
        });
        return;
      }
      case "beam": {
        const near = lerp(v2(t.x, t.y), m, 0.55);
        s.timed(900, (g, q) => g.moveTo(near.x - 12, near.y).lineTo(near.x + 12, near.y).stroke({ width: 3, color: SCORCH, alpha: 0.6 * (1 - q) }), "under");
        sparks(s, { at: near, n: 4, color: P.spark, dist: 12, len: 4, life: 180 });
        return;
      }
      case "spray":
        areaMiss(a, t);
        return;
      case "swarm":
        return; // the drones peel off on their own
      case "glyph": {
        s.timed(320, (g, p) => {
          const r = t.r + cell * (0.12 + 0.08 * p);
          g.rect(t.x - r, t.y - r, r * 2, r * 2).stroke({ width: 2.5, color: 0xe9e6df, alpha: 1 - p });
        });
        const back = edge(a, t);
        const chars = "01<>/";
        for (let j = 0; j < 6; j++) {
          const txt = new Text({ text: chars[j % chars.length]!, style: { fontFamily: FONT, fontSize: 13, fontWeight: "700", fill: P.glow } });
          txt.anchor.set(0.5);
          const vel = polar(cell * 0.8, ang(d) + Math.PI + (s.rng() - 0.5) * 2.2);
          s.add(txt, (age) => {
            const q = clamp01(age / 380);
            txt.position.set(back.x + vel.x * easeOut(q), back.y + vel.y * easeOut(q));
            txt.alpha = 1 - q;
            return age < 380;
          });
        }
        return;
      }
    }
  }

  // ── reduced motion: one short, still flash ──────────────────────────────────────────────────
  function reducedTravel(item: AttackTravelItem): void {
    const P = paletteFor(item.fx);
    const from = v2(item.attacker.x, item.attacker.y);
    const ends = item.fx.family === "missile" && item.targets.length > 0 ? item.targets : [item.to];
    s.timed(320, (g) => {
      for (const e of ends) g.moveTo(from.x, from.y).lineTo(e.x, e.y);
      g.stroke({ width: 2, color: P.glow, alpha: 0.55 });
    });
  }

  function reducedOutcome(item: AttackOutcomeItem): void {
    const P = paletteFor(item.fx);
    const t = item.at;
    if (item.outcome === "miss") {
      s.timed(320, (g) => g.rect(t.x - t.r - 3, t.y - t.r - 3, (t.r + 3) * 2, (t.r + 3) * 2).stroke({ width: 2, color: 0x9aa6b2, alpha: 0.7 }));
      return;
    }
    const w = item.outcome === "crit" ? 4 : 2.5;
    s.timed(320, (g) => g.rect(t.x - t.r - 3, t.y - t.r - 3, (t.r + 3) * 2, (t.r + 3) * 2).stroke({ width: w, color: item.outcome === "crit" ? 0xffffff : P.glow, alpha: 0.9 }));
  }

  return {
    travel(item) {
      record({ phase: "travel", family: item.fx.family, ...(item.fx.area !== undefined ? { area: true as const } : {}), ...(item.reduced ? { reduced: true as const } : {}) });
      if (item.reduced) {
        reducedTravel(item);
        return;
      }
      // A new attack starts fresh holds on its targets (a second shot at the same target must not
      // read the first shot's outcome).
      prune();
      for (const t of item.targets) pending.set(keyOf(item.attacker.id, t.id), { born: s.t });
      if (item.fx.smart === true) for (const t of item.targets) reticle(item.attacker.id, t, paletteFor(item.fx));
      // The reticle closes before the shot leaves.
      const go = () => travelLook(item);
      if (item.fx.smart === true) s.at(170, go);
      else go();
    },
    outcome(item) {
      const { fx, outcome } = item;
      record({ phase: "outcome", family: fx.family, outcome, ...(item.area ? { area: true as const } : {}), ...(item.reduced ? { reduced: true as const } : {}) });
      prune();
      const pend = pending.get(keyOf(item.attacker.id, item.at.id));
      if (outcome !== "land" && pend !== undefined) {
        pend.resAt = s.t;
        pend.resKind = outcome === "miss" ? "miss" : "hit";
      }
      if (outcome === "land") {
        detonate(fx, item.at, item.reduced);
        return;
      }
      if (item.reduced) {
        reducedOutcome(item);
        return;
      }
      const i = Math.floor(s.rng() * 2);
      if (outcome === "miss") {
        if (item.area && fx.family !== "missile" && fx.family !== "swarm") areaMiss(item.attacker, item.at);
        else missLook(item, i);
        return;
      }
      const crit = outcome === "crit";
      const k = crit ? 1.5 : 1;
      if (crit) {
        s.freeze(75);
        disc(s, { at: item.at, r0: item.at.r, r1: item.at.r + cell * 1.2, color: 0xffffff, life: 110, alpha: 0.55 });
      }
      if (item.area && fx.family !== "missile" && fx.family !== "swarm" && fx.family !== "beam" && fx.family !== "spray") areaHit(fx, { from: item.attacker, t: item.at, k });
      else hitLook(item, k);
      paletteAccent(fx, item.at, k);
    },
    clear() {
      pending.clear();
      s.clear();
    },
    log() {
      return logEntries.slice();
    },
  };
}
